import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Building2, Save, X, Image, Plus, Trash2, Star, MapPin, Phone, Mail, 
  Globe, Clock, Users, CheckCircle, Upload, Loader2, Crown, Calendar,
  ChevronDown, ChevronUp, GripVertical, ArrowLeft, DollarSign, Sparkles, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';
import { CHILEAN_REGIONS, getCitiesByRegion } from '../../constants/chileanLocations';

// Plan options
var PLAN_OPTIONS = [
  { value: 'gratuito', label: 'Gratuito', level: 0, color: 'slate' },
  { value: 'destacado', label: 'Destacado', level: 1, color: 'blue' },
  { value: 'premium', label: 'Premium', level: 2, color: 'purple' },
  { value: 'elite', label: 'Elite', level: 3, color: 'amber' },
];

// Modality options
var MODALITY_OPTIONS = [
  'Residencial',
  'Ambulatorio', 
  'Online',
  'Mixto',
  'Hospital de día',
  'Internación parcial'
];

// Service options
var SERVICE_OPTIONS = [
  'Desintoxicación',
  'Terapia individual',
  'Terapia grupal',
  'Terapia familiar',
  'Psiquiatría',
  'Psicología',
  'Trabajo social',
  'Nutrición',
  'Actividades recreativas',
  'Talleres ocupacionales',
  'Seguimiento post-tratamiento',
  'Intervención en crisis',
  'Programa 12 pasos'
];

// Addiction types
var ADDICTION_OPTIONS = [
  'Alcohol',
  'Drogas',
  'Cocaína',
  'Marihuana',
  'Opioides',
  'Medicamentos',
  'Juego patológico',
  'Adicciones comportamentales',
  'Tabaco',
  'Patología dual'
];

// Workshop options (must match CenterDashboard.js)
var WORKSHOP_OPTIONS = [
  'Teatro',
  'Meditación', 
  'Habilidades sociales',
  'Prevención de recaída',
  'Deportes',
  'Arte',
  'Música',
  'Yoga'
];

export default function CenterEditor({ center, onClose, onSave, mode = 'admin' }) {
  // mode: 'admin' | 'user' | 'sales'
  // admin: all sections
  // user: hide plan, status (verification)
  // sales: all sections except sensitive admin-only fields
  
  var [loading, setLoading] = useState(false);
  var [uploadingImage, setUploadingImage] = useState(false);
  var [activeSection, setActiveSection] = useState('info');
  
  // Form state
  var [formData, setFormData] = useState({
    // Basic info
    name: '',
    description: '',
    address: '',
    city: '',
    region: '',
    coordinates: { lat: '', lng: '' },
    
    // Contact
    phone: '',
    email: '',
    website: '',
    whatsapp: '',
    facebook: '',
    instagram: '',
    
    // Plan
    plan_type: 'gratuito',
    plan_level: 0,
    premium_until: '',
    
    // Pricing
    price_min: '',
    price_max: '',
    
    // Treatment Duration
    treatment_duration_min: '',
    treatment_duration_max: '',
    
    // Images
    logo: '',
    gallery: [],
    
    // Modality & Services
    modality: [],
    services: [],
    addictions_treated: [],
    
    // Workshops
    workshops: [],
    custom_workshops: '',
    
    // Capacity
    capacity: '',
    gender: 'mixto', // mixto, hombres, mujeres
    min_age: '',
    max_age: '',
    
    // Schedule
    schedule: '',
    
    // Status
    is_public: true,
    is_verified: false
  });

  useEffect(function() {
    if (center) {
      setFormData({
        name: center.name || '',
        description: center.description || '',
        address: center.address || '',
        city: center.city || '',
        region: center.region || '',
        coordinates: center.coordinates || { lat: '', lng: '' },
        phone: center.phone || '',
        email: center.email || '',
        website: center.website || '',
        whatsapp: center.whatsapp || '',
        facebook: center.facebook || '',
        instagram: center.instagram || '',
        plan_type: center.plan_type || 'gratuito',
        plan_level: center.plan_level || 0,
        premium_until: center.premium_until ? center.premium_until.split('T')[0] : '',
        price_min: center.price_min || '',
        price_max: center.price_max || '',
        treatment_duration_min: center.treatment_duration_min || '',
        treatment_duration_max: center.treatment_duration_max || '',
        logo: center.logo || '',
        gallery: center.gallery || [],
        modality: Array.isArray(center.modality) ? center.modality : (center.modality ? [center.modality] : []),
        services: center.services || [],
        addictions_treated: center.addictions_treated || [],
        workshops: center.workshops || [],
        custom_workshops: center.custom_workshops || '',
        capacity: center.capacity || '',
        gender: center.gender || 'mixto',
        min_age: center.min_age || '',
        max_age: center.max_age || '',
        schedule: center.schedule || '',
        is_public: center.is_public !== false,
        is_verified: center.is_verified || false
      });
    }
  }, [center]);

  function updateField(field, value) {
    setFormData(function(prev) {
      return Object.assign({}, prev, { [field]: value });
    });
  }

  function toggleArrayItem(field, item) {
    setFormData(function(prev) {
      var arr = prev[field] || [];
      if (arr.includes(item)) {
        return Object.assign({}, prev, { [field]: arr.filter(function(x) { return x !== item; }) });
      } else {
        return Object.assign({}, prev, { [field]: arr.concat([item]) });
      }
    });
  }

  function handlePlanChange(planValue) {
    var plan = PLAN_OPTIONS.find(function(p) { return p.value === planValue; });
    setFormData(function(prev) {
      return Object.assign({}, prev, { 
        plan_type: planValue,
        plan_level: plan ? plan.level : 0
      });
    });
  }

  async function handleImageUpload(e) {
    var file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen no debe superar 10MB');
      return;
    }
    
    // Check file type
    var allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    var allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];
    var ext = file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext || '')) {
      toast.error('Formato no soportado. Use JPG, PNG, WebP, GIF o HEIC');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      var formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      var res = await authAxios.post(API + '/upload/image', formDataUpload, {
        withCredentials: true
      });
      
      var imageUrl = res.data.url;
      
      // Add to gallery and set as logo if current logo is empty or broken (old domain URL)
      setFormData(function(prev) {
        var newGallery = prev.gallery.concat([imageUrl]);
        var currentLogo = prev.logo || '';
        var logoIsBroken = !currentLogo || currentLogo.includes('.emergent.host') || currentLogo.includes('.emergentagent.com');
        return Object.assign({}, prev, { 
          gallery: newGallery,
          logo: logoIsBroken ? imageUrl : currentLogo
        });
      });
      
      toast.success('Imagen subida correctamente');
    } catch (err) {
      console.error('Upload error:', err);
      var errorMsg = err.response?.data?.detail || 'Error al subir imagen';
      toast.error(errorMsg);
    }
    
    setUploadingImage(false);
  }

  function removeImage(index) {
    setFormData(function(prev) {
      var newGallery = prev.gallery.filter(function(_, i) { return i !== index; });
      var newLogo = prev.logo;
      
      // If removed image was the logo, set new logo
      if (prev.gallery[index] === prev.logo) {
        newLogo = newGallery[0] || '';
      }
      
      return Object.assign({}, prev, { gallery: newGallery, logo: newLogo });
    });
  }

  function setAsMainImage(imageUrl) {
    setFormData(function(prev) {
      return Object.assign({}, prev, { logo: imageUrl });
    });
    toast.success('Imagen principal actualizada');
  }

  async function handleSave() {
    // Validate required fields (matches CenterCreate model: name, address, city, phone)
    var missing = [];
    if (!formData.name || !formData.name.trim()) missing.push('Nombre');
    if (!formData.address || !formData.address.trim()) missing.push('Dirección');
    if (!formData.city || !formData.city.trim()) missing.push('Ciudad');
    if (!formData.phone || !formData.phone.trim()) missing.push('Teléfono');
    if (missing.length > 0) {
      toast.error('Campos obligatorios faltantes: ' + missing.join(', '));
      return;
    }

    setLoading(true);
    
    try {
      var dataToSave = Object.assign({}, formData);

      // Clean up data
      if (dataToSave.premium_until) {
        dataToSave.premium_until = new Date(dataToSave.premium_until).toISOString();
      } else {
        delete dataToSave.premium_until;
      }

      // Sanitize numeric fields: empty strings → null (Pydantic rejects "" for int|None)
      var NUMERIC_FIELDS = [
        'price_min', 'price_max', 'capacity',
        'treatment_duration_min', 'treatment_duration_max',
        'rating', 'review_count'
      ];
      NUMERIC_FIELDS.forEach(function (f) {
        var v = dataToSave[f];
        if (v === '' || v === undefined) {
          dataToSave[f] = null;
        } else if (typeof v === 'string' && v !== null) {
          var n = parseInt(v, 10);
          dataToSave[f] = Number.isNaN(n) ? null : n;
        }
      });

      // Optional email/URL fields: empty string → null
      var OPTIONAL_STRING_FIELDS = ['email', 'website', 'whatsapp', 'instagram', 'facebook'];
      OPTIONAL_STRING_FIELDS.forEach(function (f) {
        if (dataToSave[f] === '') dataToSave[f] = null;
      });

      if (center && center.center_id) {
        // Update existing center
        await authAxios.put(API + '/admin/centers/' + center.center_id, dataToSave, { withCredentials: true });
        toast.success('Centro actualizado correctamente');
      } else {
        // Create new center
        await authAxios.post(API + '/admin/centers', dataToSave, { withCredentials: true });
        toast.success('Centro creado correctamente');
      }

      if (onSave) onSave();
      if (onClose) onClose();
    } catch (err) {
      console.error('Save error:', err);
      const msg = (function formatErr(e) {
        const d = e?.response?.data?.detail;
        if (!d) return 'Error al guardar';
        if (typeof d === 'string') return d;
        if (Array.isArray(d)) {
          return d.map(function(x){
            if (typeof x === 'string') return x;
            const field = Array.isArray(x?.loc) ? x.loc.slice(1).join('.') : '';
            const m = x?.msg || 'inválido';
            return field ? field + ': ' + m : m;
          }).join(' · ');
        }
        if (typeof d === 'object') return d.msg || JSON.stringify(d);
        return String(d);
      })(err);
      toast.error(msg);
    }
    
    setLoading(false);
  }

  function getPlanColor(planType) {
    var plan = PLAN_OPTIONS.find(function(p) { return p.value === planType; });
    return plan ? plan.color : 'slate';
  }

  // Get image URL for display
  function getImageUrl(url) {
    if (!url) return null;
    if (url.includes('.emergent.host/api/') || url.includes('.emergentagent.com/api/')) {
      try { url = new URL(url).pathname; } catch(e) {}
    }
    if (url.startsWith('/api/')) {
      var base = process.env.REACT_APP_BACKEND_URL;
    if (base && !base.includes('localhost') && typeof window !== 'undefined' && !base.includes(window.location.hostname)) {
      base = window.location.origin;
    }
    return (base || '') + url;
    }
    return url;
  }

  // Define sections based on mode
  var allSections = [
    { id: 'info', label: 'Información Básica', icon: Building2, modes: ['admin', 'user', 'sales'] },
    { id: 'contact', label: 'Contacto', icon: Phone, modes: ['admin', 'user', 'sales'] },
    { id: 'pricing', label: 'Precios', icon: DollarSign, modes: ['admin', 'user', 'sales'] },
    { id: 'plan', label: 'Plan y Suscripción', icon: Crown, modes: ['admin', 'sales'] },
    { id: 'images', label: 'Galería de Imágenes', icon: Image, modes: ['admin', 'user', 'sales'] },
    { id: 'modality', label: 'Modalidad y Servicios', icon: Users, modes: ['admin', 'user', 'sales'] },
    { id: 'workshops', label: 'Talleres', icon: Sparkles, modes: ['admin', 'user', 'sales'] },
    { id: 'status', label: 'Estado', icon: CheckCircle, modes: ['admin', 'sales'] }
  ];
  
  var sections = allSections.filter(function(s) { return s.modes.includes(mode); });

  return (
    <div className="fixed inset-0 bg-black/80 z-50 overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="bg-slate-900 border-b border-slate-800 p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onClose} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="text-xl font-bold text-white">
                {center ? 'Editar Centro' : 'Nuevo Centro'}
              </h2>
              {center && (
                <p className="text-sm text-slate-400">{center.name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose} className="border-slate-700 text-slate-300">
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar Cambios
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Sidebar */}
          <div className="w-64 bg-slate-900 border-r border-slate-800 p-4 flex-shrink-0 overflow-y-auto">
            <nav className="space-y-1">
              {sections.map(function(section) {
                var Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={function() { setActiveSection(section.id); }}
                    className={'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ' + 
                      (activeSection === section.id 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'text-slate-400 hover:text-white hover:bg-slate-800')}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm">{section.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto space-y-6">
              
              {/* Información Básica */}
              {activeSection === 'info' && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-emerald-400" />
                      Información Básica
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-slate-300">Nombre del Centro *</Label>
                      <Input 
                        value={formData.name}
                        onChange={function(e) { updateField('name', e.target.value); }}
                        placeholder="Ej: Centro de Rehabilitación Esperanza"
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Descripción</Label>
                      <textarea 
                        value={formData.description}
                        onChange={function(e) { updateField('description', e.target.value); }}
                        placeholder="Describe los servicios y enfoque del centro..."
                        className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white min-h-[120px] resize-none"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Región</Label>
                        <Select value={formData.region || ''} onValueChange={function(v) { updateField('region', v); }}>
                          <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                            <SelectValue placeholder="Seleccionar región" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            {CHILEAN_REGIONS.map(function(r) { 
                              return <SelectItem key={r.value} value={r.value} className="text-slate-200">{r.label}</SelectItem>; 
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Ciudad</Label>
                        <Select value={formData.city || ''} onValueChange={function(v) { updateField('city', v); }}>
                          <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                            <SelectValue placeholder="Seleccionar ciudad" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            {(formData.region ? getCitiesByRegion(formData.region) : []).map(function(c) { 
                              return <SelectItem key={c} value={c} className="text-slate-200">{c}</SelectItem>; 
                            })}
                            <SelectItem value="otra" className="text-slate-200">Otra ciudad</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Dirección</Label>
                      <Input 
                        value={formData.address}
                        onChange={function(e) { updateField('address', e.target.value); }}
                        placeholder="Dirección completa"
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Contacto */}
              {activeSection === 'contact' && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Phone className="w-5 h-5 text-emerald-400" />
                      Información de Contacto
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Teléfono</Label>
                        <Input 
                          value={formData.phone}
                          onChange={function(e) { updateField('phone', e.target.value); }}
                          placeholder="+56 9 1234 5678"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">WhatsApp</Label>
                        <Input 
                          value={formData.whatsapp}
                          onChange={function(e) { updateField('whatsapp', e.target.value); }}
                          placeholder="+56 9 1234 5678"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Email</Label>
                      <Input 
                        value={formData.email}
                        onChange={function(e) { updateField('email', e.target.value); }}
                        placeholder="contacto@centro.cl"
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Sitio Web</Label>
                      <Input 
                        value={formData.website}
                        onChange={function(e) { updateField('website', e.target.value); }}
                        placeholder="https://www.centro.cl"
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Facebook</Label>
                        <Input 
                          value={formData.facebook}
                          onChange={function(e) { updateField('facebook', e.target.value); }}
                          placeholder="URL de Facebook"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Instagram</Label>
                        <Input 
                          value={formData.instagram}
                          onChange={function(e) { updateField('instagram', e.target.value); }}
                          placeholder="@usuario"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Precios */}
              {activeSection === 'pricing' && (
                <div className="space-y-6">
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-emerald-400" />
                        Precios del Tratamiento
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-300">Precio Mínimo (CLP/mes)</Label>
                          <Input 
                            type="number"
                            value={formData.price_min}
                            onChange={function(e) { updateField('price_min', e.target.value ? parseInt(e.target.value) : ''); }}
                            placeholder="Ej: 350000"
                            className="bg-slate-800 border-slate-700 text-white"
                          />
                          <p className="text-xs text-slate-500 mt-1">Precio más bajo del tratamiento</p>
                        </div>
                        <div>
                          <Label className="text-slate-300">Precio Máximo (CLP/mes)</Label>
                          <Input 
                            type="number"
                            value={formData.price_max}
                            onChange={function(e) { updateField('price_max', e.target.value ? parseInt(e.target.value) : ''); }}
                            placeholder="Ej: 2500000"
                            className="bg-slate-800 border-slate-700 text-white"
                          />
                          <p className="text-xs text-slate-500 mt-1">Precio más alto del tratamiento</p>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                        <h4 className="text-white font-medium mb-2">Vista Previa de Precios</h4>
                        <p className="text-slate-300">
                          {formData.price_min && formData.price_max ? (
                            <span className="text-emerald-400 font-semibold">
                              ${Number(formData.price_min).toLocaleString('es-CL')} - ${Number(formData.price_max).toLocaleString('es-CL')} CLP/mes
                            </span>
                          ) : formData.price_min ? (
                            <span className="text-emerald-400 font-semibold">
                              Desde ${Number(formData.price_min).toLocaleString('es-CL')} CLP/mes
                            </span>
                          ) : formData.price_max ? (
                            <span className="text-emerald-400 font-semibold">
                              Hasta ${Number(formData.price_max).toLocaleString('es-CL')} CLP/mes
                            </span>
                          ) : (
                            <span className="text-slate-500">No especificado</span>
                          )}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-blue-400" />
                        Duración del Tratamiento
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-300">Duración Mínima (meses)</Label>
                          <Input 
                            type="number"
                            min="1"
                            value={formData.treatment_duration_min}
                            onChange={function(e) { updateField('treatment_duration_min', e.target.value ? parseInt(e.target.value) : ''); }}
                            placeholder="Ej: 3"
                            className="bg-slate-800 border-slate-700 text-white"
                          />
                          <p className="text-xs text-slate-500 mt-1">Tiempo mínimo de tratamiento</p>
                        </div>
                        <div>
                          <Label className="text-slate-300">Duración Máxima (meses)</Label>
                          <Input 
                            type="number"
                            min="1"
                            value={formData.treatment_duration_max}
                            onChange={function(e) { updateField('treatment_duration_max', e.target.value ? parseInt(e.target.value) : ''); }}
                            placeholder="Ej: 12"
                            className="bg-slate-800 border-slate-700 text-white"
                          />
                          <p className="text-xs text-slate-500 mt-1">Tiempo máximo de tratamiento</p>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                        <h4 className="text-white font-medium mb-2">Vista Previa de Duración</h4>
                        <p className="text-slate-300">
                          {formData.treatment_duration_min && formData.treatment_duration_max ? (
                            <span className="text-blue-400 font-semibold">
                              {formData.treatment_duration_min} - {formData.treatment_duration_max} meses
                            </span>
                          ) : formData.treatment_duration_min ? (
                            <span className="text-blue-400 font-semibold">
                              Desde {formData.treatment_duration_min} meses
                            </span>
                          ) : formData.treatment_duration_max ? (
                            <span className="text-blue-400 font-semibold">
                              Hasta {formData.treatment_duration_max} meses
                            </span>
                          ) : (
                            <span className="text-slate-500">No especificado</span>
                          )}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Plan y Suscripción */}
              {activeSection === 'plan' && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Crown className="w-5 h-5 text-amber-400" />
                      Plan y Suscripción
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <Label className="text-slate-300 mb-3 block">Plan Actual</Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {PLAN_OPTIONS.map(function(plan) {
                          var isSelected = formData.plan_type === plan.value;
                          var colorClasses = {
                            slate: 'border-slate-600 bg-slate-800/50',
                            blue: 'border-blue-500 bg-blue-500/20',
                            purple: 'border-purple-500 bg-purple-500/20',
                            amber: 'border-amber-500 bg-amber-500/20'
                          };
                          var selectedClasses = {
                            slate: 'ring-2 ring-slate-400',
                            blue: 'ring-2 ring-blue-400',
                            purple: 'ring-2 ring-purple-400',
                            amber: 'ring-2 ring-amber-400'
                          };
                          
                          return (
                            <button
                              key={plan.value}
                              onClick={function() { handlePlanChange(plan.value); }}
                              className={'p-4 rounded-lg border transition-all ' + 
                                colorClasses[plan.color] + ' ' +
                                (isSelected ? selectedClasses[plan.color] : 'hover:border-opacity-70')}
                            >
                              <div className={'text-lg font-bold ' + 
                                (plan.color === 'slate' ? 'text-slate-300' : 
                                 plan.color === 'blue' ? 'text-blue-400' :
                                 plan.color === 'purple' ? 'text-purple-400' : 'text-amber-400')}>
                                {plan.label}
                              </div>
                              <div className="text-xs text-slate-400 mt-1">
                                Nivel {plan.level}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {formData.plan_type !== 'gratuito' && (
                      <div>
                        <Label className="text-slate-300">Fecha de Vencimiento</Label>
                        <Input 
                          type="date"
                          value={formData.premium_until}
                          onChange={function(e) { updateField('premium_until', e.target.value); }}
                          className="bg-slate-800 border-slate-700 text-white max-w-xs"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Dejar vacío para suscripción sin vencimiento
                        </p>
                      </div>
                    )}
                    
                    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                      <h4 className="text-white font-medium mb-2">Resumen del Plan</h4>
                      <div className="space-y-1 text-sm">
                        <p className="text-slate-400">
                          Plan: <span className={'font-medium ' + 
                            (getPlanColor(formData.plan_type) === 'amber' ? 'text-amber-400' : 
                             getPlanColor(formData.plan_type) === 'purple' ? 'text-purple-400' :
                             getPlanColor(formData.plan_type) === 'blue' ? 'text-blue-400' : 'text-slate-300')}>
                            {PLAN_OPTIONS.find(function(p) { return p.value === formData.plan_type; })?.label || 'Gratuito'}
                          </span>
                        </p>
                        <p className="text-slate-400">
                          Nivel de prioridad: <span className="text-white">{formData.plan_level}</span>
                        </p>
                        {formData.premium_until && (
                          <p className="text-slate-400">
                            Vence: <span className="text-white">{formData.premium_until}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Galería de Imágenes */}
              {activeSection === 'images' && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Image className="w-5 h-5 text-emerald-400" />
                      Galería de Imágenes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Upload button */}
                    <div className="flex items-center gap-4">
                      <label className="cursor-pointer">
                        <input 
                          type="file" 
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadingImage}
                        />
                        <div className={'flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed transition-colors ' +
                          (uploadingImage 
                            ? 'border-slate-600 text-slate-500 cursor-not-allowed' 
                            : 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10')}>
                          {uploadingImage ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Upload className="w-5 h-5" />
                          )}
                          <span>{uploadingImage ? 'Subiendo...' : 'Subir Imagen'}</span>
                        </div>
                      </label>
                      <span className="text-xs text-slate-500">Máximo 5MB por imagen</span>
                    </div>
                    
                    {/* Warning if logo is broken */}
                    {formData.logo && !formData.gallery.includes(formData.logo) && (
                      <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-orange-300 text-sm font-medium">Logo principal no encontrado en la galería</p>
                          <p className="text-slate-400 text-xs">Haz hover en una imagen y presiona la estrella para establecerla como logo principal</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Images grid */}
                    {formData.gallery.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {formData.gallery.map(function(img, index) {
                          var isMain = img === formData.logo;
                          return (
                            <div key={index} className="relative group">
                              <div className={'aspect-video rounded-lg overflow-hidden border-2 transition-all ' +
                                (isMain ? 'border-emerald-500' : 'border-slate-700')}>
                                <img 
                                  src={getImageUrl(img)} 
                                  alt={'Imagen ' + (index + 1)}
                                  className="w-full h-full object-cover"
                                  onError={function(e) { e.target.src = '/placeholder-image.jpg'; }}
                                />
                              </div>
                              
                              {/* Main badge */}
                              {isMain && (
                                <div className="absolute top-2 left-2 px-2 py-1 bg-emerald-500 text-white text-xs rounded-full flex items-center gap-1">
                                  <Star className="w-3 h-3" />
                                  Principal
                                </div>
                              )}
                              
                              {/* Action buttons */}
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                {!isMain && (
                                  <Button
                                    size="sm"
                                    onClick={function() { setAsMainImage(img); }}
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                  >
                                    <Star className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={function() { removeImage(index); }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-slate-500">
                        <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No hay imágenes en la galería</p>
                        <p className="text-sm">Sube imágenes para mostrar el centro</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Modalidad y Servicios */}
              {activeSection === 'modality' && (
                <div className="space-y-6">
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader>
                      <CardTitle className="text-white">Modalidad de Atención</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {MODALITY_OPTIONS.map(function(mod) {
                          var isSelected = formData.modality.includes(mod);
                          return (
                            <button
                              key={mod}
                              onClick={function() { toggleArrayItem('modality', mod); }}
                              className={'px-4 py-2 rounded-lg border transition-all ' +
                                (isSelected 
                                  ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' 
                                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600')}
                            >
                              {mod}
                            </button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader>
                      <CardTitle className="text-white">Servicios Ofrecidos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {SERVICE_OPTIONS.map(function(service) {
                          var isSelected = formData.services.includes(service);
                          return (
                            <button
                              key={service}
                              onClick={function() { toggleArrayItem('services', service); }}
                              className={'px-3 py-1.5 rounded-lg border text-sm transition-all ' +
                                (isSelected 
                                  ? 'bg-blue-500/20 border-blue-500 text-blue-400' 
                                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600')}
                            >
                              {service}
                            </button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader>
                      <CardTitle className="text-white">Adicciones Tratadas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {ADDICTION_OPTIONS.map(function(addiction) {
                          var isSelected = formData.addictions_treated.includes(addiction);
                          return (
                            <button
                              key={addiction}
                              onClick={function() { toggleArrayItem('addictions_treated', addiction); }}
                              className={'px-3 py-1.5 rounded-lg border text-sm transition-all ' +
                                (isSelected 
                                  ? 'bg-purple-500/20 border-purple-500 text-purple-400' 
                                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600')}
                            >
                              {addiction}
                            </button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader>
                      <CardTitle className="text-white">Capacidad y Horarios</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-slate-300">Capacidad (pacientes)</Label>
                          <Input 
                            type="number"
                            value={formData.capacity}
                            onChange={function(e) { updateField('capacity', e.target.value); }}
                            placeholder="Ej: 50"
                            className="bg-slate-800 border-slate-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Género</Label>
                          <select
                            value={formData.gender}
                            onChange={function(e) { updateField('gender', e.target.value); }}
                            className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                          >
                            <option value="mixto">Mixto</option>
                            <option value="hombres">Solo hombres</option>
                            <option value="mujeres">Solo mujeres</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-slate-300">Horario de Atención</Label>
                          <Input 
                            value={formData.schedule}
                            onChange={function(e) { updateField('schedule', e.target.value); }}
                            placeholder="Ej: Lun-Vie 9:00-18:00"
                            className="bg-slate-800 border-slate-700 text-white"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Talleres */}
              {activeSection === 'workshops' && (
                <div className="space-y-6">
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                        Talleres Disponibles
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-400 text-sm mb-4">Selecciona los talleres que ofrece el centro</p>
                      <div className="flex flex-wrap gap-2">
                        {WORKSHOP_OPTIONS.map(function(workshop) {
                          var isSelected = formData.workshops.includes(workshop);
                          return (
                            <button
                              key={workshop}
                              onClick={function() { toggleArrayItem('workshops', workshop); }}
                              className={'px-3 py-1.5 rounded-lg border text-sm transition-all ' +
                                (isSelected 
                                  ? 'bg-purple-500/20 border-purple-500 text-purple-400' 
                                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600')}
                            >
                              {workshop}
                            </button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader>
                      <CardTitle className="text-white">Otros Talleres</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-400 text-sm mb-4">Escribe un taller y presiona Enter para agregarlo</p>
                      
                      {/* Custom workshops tags display */}
                      {formData.custom_workshops && formData.custom_workshops.trim() && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {formData.custom_workshops.split(',').filter(function(t) { return t.trim(); }).map(function(tag, idx) {
                            return (
                              <span key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500 text-emerald-400 rounded-lg text-sm">
                                {tag.trim()}
                                <button
                                  type="button"
                                  onClick={function() {
                                    var tags = formData.custom_workshops.split(',').filter(function(t) { return t.trim(); });
                                    tags.splice(idx, 1);
                                    updateField('custom_workshops', tags.join(', '));
                                  }}
                                  className="ml-1 hover:text-red-400"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      
                      <Input 
                        placeholder="Escribe y presiona Enter..."
                        className="bg-slate-800 border-slate-700 text-white"
                        onKeyDown={function(e) {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            var newTag = e.target.value.trim();
                            if (newTag) {
                              var currentTags = formData.custom_workshops ? formData.custom_workshops.split(',').filter(function(t) { return t.trim(); }) : [];
                              if (!currentTags.includes(newTag)) {
                                currentTags.push(newTag);
                                updateField('custom_workshops', currentTags.join(', '));
                              }
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Estado */}
              {activeSection === 'status' && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      Estado del Centro
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <label className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700 cursor-pointer hover:border-slate-600">
                        <div>
                          <p className="text-white font-medium">Público</p>
                          <p className="text-sm text-slate-400">El centro aparece en los listados públicos</p>
                        </div>
                        <input 
                          type="checkbox"
                          checked={formData.is_public}
                          onChange={function(e) { updateField('is_public', e.target.checked); }}
                          className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
                        />
                      </label>
                      
                      <label className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700 cursor-pointer hover:border-slate-600">
                        <div>
                          <p className="text-white font-medium">Verificado</p>
                          <p className="text-sm text-slate-400">Muestra el badge de verificación en el perfil</p>
                        </div>
                        <input 
                          type="checkbox"
                          checked={formData.is_verified}
                          onChange={function(e) { updateField('is_verified', e.target.checked); }}
                          className="w-5 h-5 rounded border-slate-600 text-blue-500 focus:ring-blue-500"
                        />
                      </label>
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
